import React, { useState } from 'react';
import { DiagnosticTest, Language } from '../types';
import { translations } from '../translations';
import { ClipboardCheck, Info, ArrowRight, CheckCircle2 } from 'lucide-react';

interface Props {
  tests: DiagnosticTest[];
  onSubmit: (feedback: Record<string, string>) => void;
  lang: Language;
  initialFeedback?: Record<string, string>;
}

export const DiagnosticPanel: React.FC<Props> = ({ tests, onSubmit, lang, initialFeedback = {} }) => {
  const t = translations[lang];
  const [feedback, setFeedback] = useState<Record<string, string>>(initialFeedback);

  const handleInputChange = (testName: string, value: string) => {
    setFeedback(prev => ({ ...prev, [testName]: value }));
  };

  const isComplete = tests.every(test => feedback[test.test] && feedback[test.test].trim().length > 0);

  return (
    <div className="bg-white rounded-2xl shadow-xl border border-stone-200 overflow-hidden animate-fade-in max-w-5xl mx-auto">
      <div className="bg-stone-800 p-4 md:p-6 text-white flex items-center gap-3">
        <ClipboardCheck className="text-emerald-400" size={24} />
        <div>
          <h3 className="font-serif-sc font-bold text-lg">{t.diagnosticTitle}</h3>
          <p className="text-xs text-stone-400">{t.diagnosticSub}</p>
        </div>
      </div>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {tests.map((test, index) => (
            <div key={index} className="space-y-3 p-4 bg-stone-50 rounded-xl border border-stone-100 hover:border-emerald-200 transition-all">
              <div className="flex items-center gap-2 text-stone-800 font-bold">
                <span className="w-6 h-6 bg-stone-800 text-white rounded-full flex items-center justify-center text-[10px]">{index + 1}</span>
                <span className="font-serif-sc">{test.test}</span>
              </div>
              
              <div className="flex items-start gap-2 text-xs text-stone-500 bg-white p-2 rounded border border-stone-100">
                <Info size={14} className="shrink-0 mt-0.5 text-amber-500" />
                <p>{test.instruction}</p>
              </div>

              <div className="space-y-2">
                <label className="text-[10px] font-bold text-stone-400 uppercase tracking-widest">{t.testResult}</label>
                <textarea
                  value={feedback[test.test] || ''}
                  onChange={(e) => handleInputChange(test.test, e.target.value)}
                  placeholder={test.expected}
                  className="w-full p-3 text-sm bg-white border border-stone-200 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none transition-all font-serif-sc min-h-[80px]"
                />
              </div>
            </div>
          ))}
        </div>

        <div className="pt-4 border-t border-stone-100 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-stone-500 text-xs italic">
            <CheckCircle2 size={16} className={isComplete ? "text-emerald-500" : "text-stone-300"} />
            <span>{isComplete ? t.diagnosticReady : t.diagnosticPending}</span>
          </div>
          
          <button
            onClick={() => onSubmit(feedback)}
            disabled={!isComplete}
            className="w-full md:w-auto px-8 py-3 bg-stone-800 hover:bg-stone-900 disabled:bg-stone-200 text-white rounded-xl font-serif-sc font-bold shadow-lg transition-all flex items-center justify-center gap-2 group"
          >
            <span>{t.submitDiagnostic}</span>
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </button>
        </div>
      </div>
    </div>
  );
};
